import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Chip,
  IconButton,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Person,
  Business,
  Edit,
  Save,
  Public,
  VisibilityOff,
  Security,
  CheckCircle,
  Star,
  PhotoCamera,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import API from '../../API';

/**
 * Unified Profile Settings Component
 * Works for both User and Creator contexts
 * Automatically detects which context is active and displays appropriate fields
 */
export default function UnifiedProfileSettings({
  onUploadPhoto,
  compact = false,
  showContext = true
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  
  // Auth hooks
  const { isAuthorized: isUserAuthorized, user: regularUser } = useAuth();
  const {
    isAuthorized: isCreatorAuthorized,
    user: creatorUser,
    isInitialized
  } = useCreatorAuth();
  
  // State
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [photoUploadInProgress, setPhotoUploadInProgress] = useState(false);
  
  // User profile state
  const [userProfile, setUserProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    address: '',
    phoneNumber: '',
    isPrivate: false,
    blitzboltsPublic: false,
    phphref: '',
  });
  
  // Creator profile state
  const [creatorProfile, setCreatorProfile] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    race: '',
    gender: '',
    location: '',
    pfphref: '',
    region: '',
  });

  const normalizeVisibilityValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'public'].includes(normalized)) return true;
      if (['false', '0', 'no', 'private'].includes(normalized)) return false;
    }
    return false;
  };

  const resolveBlitzboltsPublicSetting = (settings) => {
    if (!settings || typeof settings !== 'object') return false;

    const rawValue =
      settings.blitzbolts_public ??
      settings.blitzboltsPublic ??
      settings.blitz_bolts_public ??
      settings.blitz_bolt_public ??
      settings.blitz_bold_public;

    return normalizeVisibilityValue(rawValue);
  };
  
  // Wait for creator auth to initialize
  if (!isInitialized) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Initializing...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }
  
  // Determine active context
  const isCreatorContext = isCreatorAuthorized;
  const isUserContext = isUserAuthorized;
  
  // No context - neither user nor creator is authorized
  if (!isCreatorContext && !isUserContext) {
    return (
      <Alert severity="warning">
        Unable to determine profile context. Please ensure you're logged in.
      </Alert>
    );
  }
  
  // Fetch user data
  const { data: userData, isLoading: userLoading, refetch: refetchUser } = useQuery(
    ['user'],
    API.users.fetchUser,
    {
      enabled: isUserContext,
      refetchOnWindowFocus: false,
    }
  );

  const { data: userSettings, refetch: refetchUserSettings } = useQuery(
    ['unifiedUserSettings'],
    API.users.fetchUserSettings,
    {
      enabled: isUserContext,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  
  // Fetch creator data
  const { data: creatorData, isLoading: creatorLoading, refetch: refetchCreator } = useQuery(
    ['creator'],
    API.creatorConnect.getCreator,
    {
      enabled: isCreatorContext,
      refetchOnWindowFocus: false,
    }
  );
  
  // Sync user data with local state whenever it changes
  useEffect(() => {
    if (userData && isUserContext) {
      setUserProfile((prev) => ({
        ...prev,
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email || '',
        username: userData.username || '',
        address: userData.address || '',
        phoneNumber: userData.phone_number || '',
        isPrivate: userData.is_private || false,
        phphref: userData.phphref || '',
      }));
    }
  }, [userData, isUserContext]);

  useEffect(() => {
    if (!isUserContext) return;
    setUserProfile((prev) => ({
      ...prev,
      blitzboltsPublic: resolveBlitzboltsPublicSetting(userSettings),
    }));
  }, [isUserContext, userSettings]);
  
  // Sync creator data with local state whenever it changes
  useEffect(() => {
    if (creatorData?.user?.creator && isCreatorContext) {
      const cr = creatorData.user.creator;
      setCreatorProfile({
        displayName: cr.display_name || '',
        email: cr.email || '',
        phoneNumber: cr.phone_number || '',
        race: cr.ethnicity || '',
        gender: cr.gender || '',
        location: cr.geolocation || '',
        pfphref: cr.pfphref || '',
        region: cr.region || '',
      });
    }
  }, [creatorData, isCreatorContext]);
  
  // Save user mutation
  const saveUserMutation = useMutation(
    (data) => API.users.updateUser(data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['user']);
        await refetchUser();
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      },
      onError: (error) => {
        setSaveError(error?.response?.data?.error || 'Failed to save profile');
        setTimeout(() => setSaveError(''), 5000);
      },
    }
  );

  const saveUserSettingsMutation = useMutation(
    (payload) =>
      userSettings?.id
        ? API.users.updateUserSettings({ id: userSettings.id, ...payload })
        : API.users.createUserSettings(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['unifiedUserSettings']);
      },
      onError: (error) => {
        setSaveError(error?.response?.data?.error || 'Failed to save user settings');
        setTimeout(() => setSaveError(''), 5000);
      },
    }
  );
  
  // Save creator mutation
  const saveCreatorMutation = useMutation(
    (data) => API.creatorConnect.editCreator(data),
    {
      onSuccess: async () => {
        // Import query keys for proper invalidation
        const { queryKeys } = await import('../../API/queryKeys');
        
        // Invalidate all creator-related queries
        await queryClient.invalidateQueries(queryKeys.creator.all);
        await queryClient.invalidateQueries(['creator']);
        
        // Force refetch to ensure we have latest data
        await refetchCreator();
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      },
      onError: (error) => {
        setSaveError(error?.response?.data?.error || 'Failed to save profile');
        setTimeout(() => setSaveError(''), 5000);
      },
    }
  );
  
  // Handle save
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      if (isCreatorContext) {
        await saveCreatorMutation.mutateAsync({
          display_name: creatorProfile.displayName,
          email: creatorProfile.email,
          phone_number: creatorProfile.phoneNumber,
          ethnicity: creatorProfile.race,
          gender: creatorProfile.gender,
          geolocation: creatorProfile.location,
          pfphref: creatorProfile.pfphref,
          region: creatorProfile.region,
        });
      } else if (isUserContext) {
        await saveUserMutation.mutateAsync({
          first_name: userProfile.firstName,
          last_name: userProfile.lastName,
          email: userProfile.email,
          username: userProfile.username,
          address: userProfile.address,
          phone_number: userProfile.phoneNumber,
          is_private: userProfile.isPrivate,
          phphref: userProfile.phphref,
        });
        await saveUserSettingsMutation.mutateAsync({
          blitzbolts_public: userProfile.blitzboltsPublic === true,
        });
        await refetchUserSettings();
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    isCreatorContext,
    isUserContext,
    creatorProfile,
    userProfile,
    saveCreatorMutation,
    saveUserMutation,
    saveUserSettingsMutation,
    refetchUserSettings,
  ]);
  
  // Handle profile photo update
  const handleProfilePhotoUpdate = useCallback(async (uploadedUrl) => {
    if (!uploadedUrl) {
      setSaveError('No photo URL provided');
      return;
    }
    
    setPhotoUploadInProgress(true);
    setSaveError('');
    
    try {
      // Update the local state first
      if (isCreatorContext) {
        const updatedProfile = { ...creatorProfile, pfphref: uploadedUrl };
        setCreatorProfile(updatedProfile);
        
        // Wait a tick for state to update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Save to backend
        await saveCreatorMutation.mutateAsync({
          display_name: updatedProfile.displayName,
          email: updatedProfile.email,
          phone_number: updatedProfile.phoneNumber,
          ethnicity: updatedProfile.race,
          gender: updatedProfile.gender,
          geolocation: updatedProfile.location,
          pfphref: uploadedUrl,
          region: updatedProfile.region,
        });
      } else if (isUserContext) {
        const updatedProfile = { ...userProfile, phphref: uploadedUrl };
        setUserProfile(updatedProfile);
        
        // Wait a tick for state to update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Save to backend
        await saveUserMutation.mutateAsync({
          first_name: updatedProfile.firstName,
          last_name: updatedProfile.lastName,
          email: updatedProfile.email,
          username: updatedProfile.username,
          address: updatedProfile.address,
          phone_number: updatedProfile.phoneNumber,
          is_private: updatedProfile.isPrivate,
          phphref: uploadedUrl,
        });
      }
      
      // Force refetch after successful save to ensure UI is in sync
      if (isCreatorContext) {
        await refetchCreator();
      } else {
        await refetchUser();
      }
      
      // Success notification
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Profile photo update error:', error);
      setSaveError(error?.response?.data?.error || 'Failed to save profile photo');
      setTimeout(() => setSaveError(''), 5000);
    } finally {
      setPhotoUploadInProgress(false);
    }
  }, [
    isCreatorContext,
    isUserContext,
    creatorProfile,
    userProfile,
    saveCreatorMutation,
    saveUserMutation,
    refetchCreator,
    refetchUser
  ]);

  const isLoading = userLoading || creatorLoading;
  
  // Get current profile photo URL
  const currentPhotoUrl = isCreatorContext
    ? creatorProfile.pfphref
    : userProfile.phphref;
  
  // Get display name
  const displayName = isCreatorContext
    ? creatorProfile.displayName
    : `${userProfile.firstName} ${userProfile.lastName}`.trim() || userProfile.username;
  
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Loading profile...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Paper
      elevation={compact ? 0 : 3}
      sx={{
        p: compact ? 2 : 4,
        borderRadius: compact ? 2 : 4,
        border: compact ? `1px solid ${theme.palette.divider}` : 'none',
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={3}
          alignItems={isMobile ? 'center' : 'flex-start'}
        >
          {/* Profile Photo */}
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={currentPhotoUrl}
              sx={{
                width: 120,
                height: 120,
                border: `4px solid ${theme.palette.primary.main}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            >
              {!currentPhotoUrl && (isCreatorContext ? <Star sx={{ fontSize: 48 }} /> : <Person sx={{ fontSize: 48 }} />)}
            </Avatar>
            <IconButton
              onClick={() => {
                if (onUploadPhoto && typeof onUploadPhoto === 'function') {
                  onUploadPhoto(handleProfilePhotoUpdate);
                }
              }}
              disabled={photoUploadInProgress || isSaving}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.action.disabledBackground,
                },
              }}
            >
              {photoUploadInProgress ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <PhotoCamera />
              )}
            </IconButton>
          </Box>
          
          {/* Profile Info */}
          <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'center' : 'left' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {displayName || 'User Profile'}
            </Typography>
            {showContext && (
              <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <Chip
                  icon={isCreatorContext ? <Star /> : <Person />}
                  label={isCreatorContext ? 'Creator Account' : 'User Account'}
                  color={isCreatorContext ? 'secondary' : 'primary'}
                  size="small"
                />
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary">
              {isCreatorContext
                ? creatorProfile.email
                : userProfile.email}
            </Typography>
          </Box>
        </Stack>
        
        {/* Success/Error Messages */}
        {saveSuccess && (
          <Alert
            icon={<CheckCircle />}
            severity="success"
            sx={{ mt: 2 }}
            onClose={() => setSaveSuccess(false)}
          >
            Profile {photoUploadInProgress ? 'photo' : 'changes'} saved successfully!
          </Alert>
        )}
        {saveError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            onClose={() => setSaveError('')}
          >
            {saveError}
          </Alert>
        )}
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Form Fields */}
      <Box>
        <Grid container spacing={3}>
          {isCreatorContext ? (
            // Creator Fields
            <>
              <Grid item xs={12}>
                <TextField
                  label="Display Name"
                  value={creatorProfile.displayName}
                  onChange={(e) =>
                    setCreatorProfile((prev) => ({ ...prev, displayName: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={creatorProfile.email}
                  onChange={(e) =>
                    setCreatorProfile((prev) => ({ ...prev, email: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone Number"
                  value={creatorProfile.phoneNumber}
                  onChange={(e) =>
                    setCreatorProfile((prev) => ({ ...prev, phoneNumber: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Region"
                  value={creatorProfile.region}
                  onChange={(e) =>
                    setCreatorProfile((prev) => ({ ...prev, region: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                  placeholder="e.g., North America"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Location"
                  value={creatorProfile.location}
                  onChange={(e) =>
                    setCreatorProfile((prev) => ({ ...prev, location: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                  placeholder="e.g., New York, NY"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Ethnicity"
                  value={creatorProfile.race}
                  onChange={(e) =>
                    setCreatorProfile((prev) => ({ ...prev, race: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Gender"
                  value={creatorProfile.gender}
                  onChange={(e) =>
                    setCreatorProfile((prev) => ({ ...prev, gender: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
            </>
          ) : (
            // User Fields
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  label="First Name"
                  value={userProfile.firstName}
                  onChange={(e) =>
                    setUserProfile((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Last Name"
                  value={userProfile.lastName}
                  onChange={(e) =>
                    setUserProfile((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={userProfile.email}
                  onChange={(e) =>
                    setUserProfile((prev) => ({ ...prev, email: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Username"
                  value={userProfile.username}
                  onChange={(e) =>
                    setUserProfile((prev) => ({ ...prev, username: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={userProfile.address}
                  onChange={(e) =>
                    setUserProfile((prev) => ({ ...prev, address: e.target.value }))
                  }
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone Number"
                  value={userProfile.phoneNumber}
                  onChange={(e) =>
                    setUserProfile((prev) => ({ ...prev, phoneNumber: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              
              {/* Privacy Settings for Users */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security />
                  Privacy Settings
                </Typography>
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: userProfile.isPrivate ? 'action.hover' : 'background.paper',
                    border: userProfile.isPrivate ? '2px solid' : '1px solid',
                    borderColor: userProfile.isPrivate ? 'primary.main' : 'divider',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {userProfile.isPrivate ? (
                        <VisibilityOff color="primary" />
                      ) : (
                        <Public color="action" />
                      )}
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {userProfile.isPrivate ? 'Private Mode' : 'Public Profile'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {userProfile.isPrivate
                            ? 'You appear as "Anonymous User" in activity feeds'
                            : 'Your name and profile are visible in activity feeds'}
                        </Typography>
                      </Box>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userProfile.isPrivate}
                          onChange={(e) =>
                            setUserProfile((prev) => ({ ...prev, isPrivate: e.target.checked }))
                          }
                          color="primary"
                        />
                      }
                      label=""
                    />
                  </Box>
                  {userProfile.isPrivate && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{
                        backgroundColor: 'primary.50',
                        '& .MuiAlert-icon': { color: 'primary.main' },
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Privacy Mode is ON:</strong> Other users will see your activities as
                        "Anonymous User" and cannot click through to your profile.
                      </Typography>
                    </Alert>
                  )}
                </Card>

                <Card
                  variant="outlined"
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: userProfile.blitzboltsPublic ? 'success.50' : 'background.paper',
                    border: userProfile.blitzboltsPublic ? '2px solid' : '1px solid',
                    borderColor: userProfile.blitzboltsPublic ? 'success.main' : 'divider',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {userProfile.blitzboltsPublic ? (
                        <Public color="success" />
                      ) : (
                        <VisibilityOff color="action" />
                      )}
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {userProfile.blitzboltsPublic
                            ? 'BlitzBolt Achievements: Public'
                            : 'BlitzBolt Achievements: Private'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {userProfile.blitzboltsPublic
                            ? 'Other users can view your achievements from your profile.'
                            : 'Only you can view your achievement history (recommended default).'}
                        </Typography>
                      </Box>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userProfile.blitzboltsPublic}
                          onChange={(e) =>
                            setUserProfile((prev) => ({
                              ...prev,
                              blitzboltsPublic: e.target.checked,
                            }))
                          }
                          color="success"
                        />
                      }
                      label=""
                    />
                  </Box>
                  {!userProfile.blitzboltsPublic && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{
                        backgroundColor: 'info.50',
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Default secure mode:</strong> Achievement access should remain private unless you explicitly choose to share it.
                      </Typography>
                    </Alert>
                  )}
                </Card>
              </Grid>
            </>
          )}
        </Grid>
        
        {/* Save Button */}
        <Box mt={4}>
          <Button
            onClick={handleSave}
            variant="contained"
            size="large"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={isSaving || photoUploadInProgress}
            fullWidth={isMobile}
            sx={{
              background: isCreatorContext
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              '&:hover': {
                background: isCreatorContext
                  ? 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                  : 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #a0a0a0 0%, #808080 100%)',
                color: 'rgba(255,255,255,0.5)'
              }
            }}
          >
            {isSaving ? 'Saving...' : photoUploadInProgress ? 'Saving Photo...' : 'Save Profile Changes'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}